import { supabase } from "./supabase";
import type {
  AdminSpotsPage,
  AdminSpotSort,
  AdminUpdateSpotInput,
  CreateSpotInput,
  FileReportInput,
  LegalityResult,
  Meta,
  Rating,
  Spot,
  SpotPhoto,
  SpotStatus,
  UpsertRatingInput,
} from "./types";

/**
 * API surface wrapping supabase-js calls (spec 05 §2).
 *
 * Reads are anon-accessible; mutations require an authenticated session.
 * The `create_spot` RPC re-runs `check_legality` server-side (spec 05 §3) so
 * the legality check cannot be bypassed — a rejection surfaces as an error.
 */

// ---------- Spots ----------

/** Confirmed spots within the map viewport (RPC `spots_in_bbox`, anon).
 *  Returns JSON with explicit lng/lat fields (not EWKB geography). */
export async function getSpotsInBbox(
  minLng: number,
  minLat: number,
  maxLng: number,
  maxLat: number,
): Promise<Spot[]> {
  const { data, error } = await supabase.rpc("spots_in_bbox", {
    min_lng: minLng,
    min_lat: minLat,
    max_lng: maxLng,
    max_lat: maxLat,
  });
  if (error) throw error;
  const rows = (data ?? []) as Array<Record<string, unknown>>;
  return rows.map((row) => ({
    id: row.id as string,
    kind: row.kind as Spot["kind"],
    name: row.name as string,
    description: (row.description as string) ?? "",
    location: { lng: row.lng as number, lat: row.lat as number },
    legality_at_creation: (row.legality_at_creation ?? {}) as LegalityResult,
    created_by: row.created_by as string,
    created_at: row.created_at as string,
    status: (row.status as Spot["status"]) ?? "confirmed",
    rating_count: (row.rating_count as number) ?? 0,
    rating_avg: (row.rating_avg as number) ?? 0,
  }));
}

/** One spot by id (RPC with explicit lng/lat). */
export async function getSpot(id: string): Promise<Spot> {
  const { data, error } = await supabase.rpc("get_spot_by_id", {
    spot_id: id,
  });
  if (error) throw error;
  const row = data as Record<string, unknown>;
  if (!row) throw new Error("Spot not found");
  return {
    id: row.id as string,
    kind: row.kind as Spot["kind"],
    name: row.name as string,
    description: (row.description as string) ?? "",
    location: { lng: row.lng as number, lat: row.lat as number },
    legality_at_creation: (row.legality_at_creation ?? {}) as LegalityResult,
    created_by: row.created_by as string,
    created_at: row.created_at as string,
    status: (row.status as Spot["status"]) ?? "confirmed",
    rating_count: (row.rating_count as number) ?? 0,
    rating_avg: (row.rating_avg as number) ?? 0,
  };
}

/**
 * Create a spot via the `create_spot` SECURITY DEFINER RPC (authenticated).
 * Runs `check_legality` server-side and stores the result as a snapshot for
 * admin review, but does NOT block creation — legality is advisory per the
 * product design (AGENTS.md, spec 01 §5). The badge warns; the user decides.
 *
 * New spots enter with `status='pending'` and are invisible to others until an
 * admin approves them to `confirmed` (migration 20260115000012).
 */
export async function createSpot(input: CreateSpotInput): Promise<Spot> {
  const { data, error } = await supabase.rpc("create_spot", {
    kind: input.kind,
    name: input.name,
    lng: input.lng,
    lat: input.lat,
    description: input.description,
  });
  if (error) {
    // The function raises with the legality JSON in the message; surface it.
    throw new Error(error.message, { cause: error });
  }
  // create_spot returns a spots row with EWKB location — fetch via get_spot_by_id
  // to get proper lng/lat JSON.
  const rawRow = Array.isArray(data) ? data[0] : data;
  const spotId = (rawRow as Record<string, unknown>)?.id as string | undefined;
  if (spotId) {
    return getSpot(spotId);
  }
  throw new Error("create_spot returned no id");
}

/**
 * Soft-delete own spot (sets status='removed', gated by RLS owner check).
 * Spec 03 §6: spots are immutable except delete; default MVP = delete + re-create.
 */
export async function deleteSpot(id: string): Promise<void> {
  const { error } = await supabase
    .from("spots")
    .update({ status: "removed" })
    .eq("id", id);
  if (error) throw error;
}

/** Spots created by a given user (RPC with explicit lng/lat). */
export async function getSpotsByCreator(uid: string): Promise<Spot[]> {
  const { data, error } = await supabase.rpc("spots_by_creator", {
    creator_uid: uid,
  });
  if (error) throw error;
  const rows = (data ?? []) as Array<Record<string, unknown>>;
  return rows.map((row) => ({
    id: row.id as string,
    kind: row.kind as Spot["kind"],
    name: row.name as string,
    description: (row.description as string) ?? "",
    location: { lng: row.lng as number, lat: row.lat as number },
    legality_at_creation: (row.legality_at_creation ?? {}) as LegalityResult,
    created_by: row.created_by as string,
    created_at: row.created_at as string,
    status: (row.status as Spot["status"]) ?? "confirmed",
    rating_count: (row.rating_count as number) ?? 0,
    rating_avg: (row.rating_avg as number) ?? 0,
  }));
}

// ---------- Ratings ----------

/** All ratings for a spot (anon). */
export async function getRatingsForSpot(spotId: string): Promise<Rating[]> {
  const { data, error } = await supabase
    .from("ratings")
    .select("*")
    .eq("spot_id", spotId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as Rating[];
}

/** Upsert the current user's rating on a spot (one rating per user per spot). */
export async function upsertRating(input: UpsertRatingInput): Promise<Rating> {
  const { data, error } = await supabase
    .from("ratings")
    .upsert(
      {
        spot_id: input.spot_id,
        stars: input.stars,
        comment: input.comment,
      },
      { onConflict: "spot_id,created_by" },
    )
    .select()
    .single();
  if (error) throw error;
  return data as Rating;
}

/** Delete the current user's own rating on a spot. */
export async function deleteRating(spotId: string): Promise<void> {
  const { error } = await supabase
    .from("ratings")
    .delete()
    .eq("spot_id", spotId);
  if (error) throw error;
}

/** Ratings created by a given user (for "My contributions"). */
export async function getRatingsByCreator(uid: string): Promise<Rating[]> {
  const { data, error } = await supabase
    .from("ratings")
    .select("*")
    .eq("created_by", uid)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as Rating[];
}

// ---------- Legality ----------

/**
 * Live legality check via the `check_legality` RPC (anon).
 * Used by the debounced hook during pin drag (spec 06 §4–§5).
 */
export async function checkLegality(
  lng: number,
  lat: number,
  at: string = new Date().toISOString(),
): Promise<LegalityResult> {
  const { data, error } = await supabase.rpc("check_legality", {
    lng,
    lat,
    at,
  });
  if (error) throw error;
  return data as LegalityResult;
}

// ---------- Zones & reports ----------

/** Zone overlay version, for tile cache busting (spec 06 §5). */
export async function getZoneVersion(): Promise<string | null> {
  const { data, error } = await supabase
    .from("meta")
    .select("*")
    .eq("key", "zones_version")
    .maybeSingle() as { data: Meta | null; error: null };
  if (error) throw error;
  return data?.value ?? null;
}

/**
 * Fetch no-consumption zones (100m-buffered facilities + pedestrian zones)
 * for the current map viewport as a GeoJSON FeatureCollection.
 * Used to render the zone overlay dynamically without PMTiles.
 */
export async function getZonesInBbox(
  minLng: number,
  minLat: number,
  maxLng: number,
  maxLat: number,
): Promise<GeoJSON.FeatureCollection> {
  const { data, error } = await supabase.rpc("zones_in_bbox", {
    min_lng: minLng,
    min_lat: minLat,
    max_lng: maxLng,
    max_lat: maxLat,
  });
  if (error) throw error;
  return data as GeoJSON.FeatureCollection;
}

/** File a moderation report (authenticated). */
export async function fileReport(input: FileReportInput): Promise<void> {
  const { error } = await supabase.from("reports").insert({
    target_type: input.target_type,
    target_id: input.target_id,
    reason: input.reason,
    note: input.note ?? "",
  });
  if (error) throw error;
}

// ---------- Photos (Supabase Storage bucket 'spot-photos') ----------

const PHOTOS_BUCKET = "spot-photos";

/** Public URL for a stored photo object (bucket is public-read). */
export function getSpotPhotoUrl(path: string): string {
  return supabase.storage.from(PHOTOS_BUCKET).getPublicUrl(path).data.publicUrl;
}

/**
 * Upload one photo for a spot and record the `spot_photos` row.
 * Called after `createSpot` returns (a spot id is required to associate the
 * photo). The storage path convention is `{spot_id}/{photo_id}.{ext}`.
 */
export async function uploadSpotPhoto(
  spotId: string,
  file: File,
): Promise<SpotPhoto> {
  const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
  const photoId = crypto.randomUUID();
  const path = `${spotId}/${photoId}.${ext}`;

  const { error: upErr } = await supabase.storage
    .from(PHOTOS_BUCKET)
    .upload(path, file, {
      cacheControl: "3600",
      upsert: false,
      contentType: file.type || "image/jpeg",
    });
  if (upErr) throw upErr;

  const { data, error } = await supabase
    .from("spot_photos")
    .insert({ spot_id: spotId, path })
    .select()
    .single();
  if (error) {
    // Best-effort cleanup of the orphaned storage object.
    void supabase.storage.from(PHOTOS_BUCKET).remove([path]);
    throw error;
  }
  return data as SpotPhoto;
}

/** All photos for a spot (anon-readable metadata; bucket is public-read). */
export async function listSpotPhotos(spotId: string): Promise<SpotPhoto[]> {
  const { data, error } = await supabase
    .from("spot_photos")
    .select("*")
    .eq("spot_id", spotId)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data ?? []) as SpotPhoto[];
}

// ---------- Admin / moderation ----------

/** Whether the current user is in the `admins` table (gates the #admin UI). */
export async function isCurrentUserAdmin(): Promise<boolean> {
  const { data, error } = await supabase.rpc("is_current_user_admin");
  if (error) throw error;
  return Boolean(data);
}

/** The pending review queue (admin only; RPC returns JSON with explicit lng/lat). */
export async function getPendingSpots(): Promise<Spot[]> {
  const { data, error } = await supabase.rpc("pending_spots");
  if (error) throw error;
  const rows = (data ?? []) as Array<Record<string, unknown>>;
  return rows.map((row) => ({
    id: row.id as string,
    kind: row.kind as Spot["kind"],
    name: row.name as string,
    description: (row.description as string) ?? "",
    location: { lng: row.lng as number, lat: row.lat as number },
    legality_at_creation: (row.legality_at_creation ?? {}) as LegalityResult,
    created_by: row.created_by as string,
    created_at: row.created_at as string,
    status: (row.status as Spot["status"]) ?? "pending",
    rating_count: (row.rating_count as number) ?? 0,
    rating_avg: (row.rating_avg as number) ?? 0,
  }));
}

/**
 * Move a spot to a new status via the `admin_set_spot_status` SECURITY DEFINER
 * RPC (admin only). This is the sole path to publish/hide/remove a spot.
 */
export async function adminSetSpotStatus(
  spotId: string,
  status: SpotStatus,
): Promise<Spot> {
  const { data, error } = await supabase.rpc("admin_set_spot_status", {
    spot_id: spotId,
    new_status: status,
  });
  if (error) throw new Error(error.message, { cause: error });
  return parseSpotRow(Array.isArray(data) ? data[0] : data);
}

// ---------- Admin: list / update / delete (migration 20260115000013) ----------

/**
 * Paginated, filterable list of every spot — admin only
 * (migration 20260115000013 §1). Returns { items, total }.
 *
 * Pass `status: "all"` (or null) to skip the status filter; pass a free-text
 * `search` to filter by name (case-insensitive ILIKE, server-side).
 */
export async function adminListSpots(params: {
  status?: SpotStatus | "all" | null;
  search?: string;
  limit?: number;
  offset?: number;
  sort?: AdminSpotSort;
  order?: "asc" | "desc";
}): Promise<AdminSpotsPage> {
  const status = params.status && params.status !== "all" ? params.status : null;
  const { data, error } = await supabase.rpc("admin_list_spots", {
    p_status: status,
    p_search: (params.search ?? "").trim() || null,
    p_limit: params.limit ?? 50,
    p_offset: params.offset ?? 0,
    p_sort: params.sort ?? "created_at",
    p_order: params.order ?? "desc",
  });
  if (error) throw new Error(error.message, { cause: error });
  const payload = (data ?? { items: [], total: 0 }) as {
    items: Array<Record<string, unknown>>;
    total?: number;
  };
  return {
    items: (payload.items ?? []).map(parseAdminListItem),
    total: typeof payload.total === "number" ? payload.total : 0,
  };
}

/**
 * Edit a spot's fields — admin only (migration 20260115000013 §2).
 * Only the provided (non-null) fields are written; the RPC returns the updated
 * `spots` row (parsed via parseSpotRow for consistent lng/lat handling).
 */
export async function adminUpdateSpot(
  spotId: string,
  input: AdminUpdateSpotInput,
): Promise<Spot> {
  const { data, error } = await supabase.rpc("admin_update_spot", {
    spot_id: spotId,
    p_name: input.name ?? null,
    p_description: input.description ?? null,
    p_kind: input.kind ?? null,
    p_lng: input.lng ?? null,
    p_lat: input.lat ?? null,
  });
  if (error) throw new Error(error.message, { cause: error });
  return parseSpotRow(Array.isArray(data) ? data[0] : data);
}

/**
 * Soft-delete (status='removed', default) or hard-delete a spot — admin only
 * (migration 20260115000013 §3). Hard delete is irreversible and cascades to
 * ratings/reports/spot_photos rows.
 */
export async function adminDeleteSpot(
  spotId: string,
  hard = false,
): Promise<void> {
  const { error } = await supabase.rpc("admin_delete_spot", {
    spot_id: spotId,
    p_hard: hard,
  });
  if (error) throw new Error(error.message, { cause: error });
}

// ---------- Helpers ----------

/**
 * Parse a raw PostgREST spot row into the client Spot shape.
 * PostgREST returns the `location` geography column as a GeoJSON Feature by
 * default when selected as `location`; we also handle the raw EWKT/`{x,y}` forms
 * defensively so a missing `geojson` representation doesn't break the UI.
 */
function parseSpotRow(row: Record<string, unknown>): Spot {
  const rawLoc = row.location as Record<string, unknown> | null;

  let lng = 0;
  let lat = 0;
  if (rawLoc && typeof rawLoc === "object") {
    const coords = rawLoc.coordinates;
    const x = rawLoc.x;
    const y = rawLoc.y;
    if (Array.isArray(coords) && coords.length >= 2) {
      lng = Number(coords[0]);
      lat = Number(coords[1]);
    } else if (typeof x === "number" && typeof y === "number") {
      lng = x;
      lat = y;
    }
  }

  return {
    id: row.id as string,
    kind: row.kind as Spot["kind"],
    name: row.name as string,
    description: (row.description as string) ?? "",
    location: { lng, lat },
    legality_at_creation: (row.legality_at_creation ?? {}) as LegalityResult,
    created_by: row.created_by as string,
    created_at: row.created_at as string,
    status: (row.status as Spot["status"]) ?? "active",
    rating_count: (row.rating_count as number) ?? 0,
    rating_avg: (row.rating_avg as number) ?? 0,
  };
}

/**
 * Parse one item from `admin_list_spots`. Unlike parseSpotRow, the RPC returns
 * explicit numeric `lng`/`lat` columns (from ST_X/ST_Y), so no geometry
 * coercion is needed. Falls back to parseSpotRow if the shape ever drifts.
 */
function parseAdminListItem(row: Record<string, unknown>): Spot {
  return {
    id: row.id as string,
    kind: row.kind as Spot["kind"],
    name: row.name as string,
    description: (row.description as string) ?? "",
    location: {
      lng: (row.lng as number) ?? 0,
      lat: (row.lat as number) ?? 0,
    },
    legality_at_creation: (row.legality_at_creation ?? {}) as LegalityResult,
    created_by: row.created_by as string,
    created_at: row.created_at as string,
    status: (row.status as Spot["status"]) ?? "pending",
    rating_count: (row.rating_count as number) ?? 0,
    rating_avg: (row.rating_avg as number) ?? 0,
  };
}
